// frontend/src/components/common/RichTextEditor.jsx
// Custom rich text editor using contentEditable - No external dependencies
// Compatible with React 19

import React, { useRef, useCallback, useEffect } from "react";

const RichTextEditor = ({
  value,
  onChange,
  placeholder = "Enter content...",
  maxSizeMB = 2,
  minHeight = "200px",
  readOnly = false,
}) => {
  const editorRef = useRef(null);
  const isInternalChange = useRef(false);

  // Initialize content
  useEffect(() => {
    if (editorRef.current && !isInternalChange.current) {
      if (editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = value || "";
      }
    }
    isInternalChange.current = false;
  }, [value]);

  // Handle content changes
  const handleInput = useCallback(() => {
    if (editorRef.current) {
      isInternalChange.current = true;
      onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);

  // Execute formatting command
  const execCommand = useCallback((command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleInput();
  }, [handleInput]);

  // Convert file to base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  // Insert image at cursor position
  const insertImage = useCallback(async (file) => {
    const maxSizeBytes = maxSizeMB * 1024 * 1024;

    if (!file.type.startsWith("image/")) {
      alert("Please select an image file (PNG, JPG, GIF, etc.)");
      return;
    }

    if (file.size > maxSizeBytes) {
      alert(`Image is too large. Maximum size is ${maxSizeMB}MB.`);
      return;
    }

    try {
      const base64 = await fileToBase64(file);
      execCommand("insertImage", base64);
    } catch (err) {
      console.error("Failed to insert image:", err);
      alert("Failed to process image. Please try again.");
    }
  }, [maxSizeMB, execCommand]);

  // Handle paste event
  const handlePaste = useCallback((e) => {
    const clipboardData = e.clipboardData;
    if (!clipboardData) return;

    // Check for images
    const items = clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf("image") !== -1) {
        e.preventDefault();
        const file = items[i].getAsFile();
        if (file) {
          insertImage(file);
        }
        return;
      }
    }

    // For text, let default paste happen but strip dangerous content
    // (Browser handles this naturally with contentEditable)
  }, [insertImage]);

  // Handle drop event
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      for (let i = 0; i < files.length; i++) {
        if (files[i].type.startsWith("image/")) {
          insertImage(files[i]);
          break;
        }
      }
    }
  }, [insertImage]);

  // Handle file upload button
  const handleFileUpload = useCallback(() => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (file) {
        await insertImage(file);
      }
    };
    input.click();
  }, [insertImage]);

  // Handle link insertion
  const handleLink = useCallback(() => {
    const url = prompt("Enter URL:", "https://");
    if (url) {
      execCommand("createLink", url);
    }
  }, [execCommand]);

  // Toolbar button component
  const ToolbarButton = ({ command, icon, title, onClick, active }) => (
    <button
      type="button"
      className={`btn btn-sm btn-outline-secondary ${active ? "active" : ""}`}
      title={title}
      onMouseDown={(e) => e.preventDefault()} // Prevent focus loss
      onClick={onClick || (() => execCommand(command))}
      disabled={readOnly}
    >
      <i className={`bi bi-${icon}`}></i>
    </button>
  );

  // Toolbar separator
  const Separator = () => <span className="border-start mx-1" style={{ height: "24px" }}></span>;

  return (
    <div className="rich-text-editor-container">
      {/* Toolbar */}
      {!readOnly && (
        <div className="btn-toolbar p-2 bg-light border rounded-top gap-1 flex-wrap" role="toolbar">
          {/* Text formatting */}
          <div className="btn-group btn-group-sm me-1">
            <ToolbarButton command="bold" icon="type-bold" title="Bold (Ctrl+B)" />
            <ToolbarButton command="italic" icon="type-italic" title="Italic (Ctrl+I)" />
            <ToolbarButton command="underline" icon="type-underline" title="Underline (Ctrl+U)" />
            <ToolbarButton command="strikeThrough" icon="type-strikethrough" title="Strikethrough" />
          </div>

          <Separator />

          {/* Headers */}
          <div className="btn-group btn-group-sm me-1">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary dropdown-toggle"
              data-bs-toggle="dropdown"
              title="Heading"
              disabled={readOnly}
            >
              <i className="bi bi-type-h1"></i>
            </button>
            <ul className="dropdown-menu">
              <li><button className="dropdown-item" onClick={() => execCommand("formatBlock", "h1")}>Heading 1</button></li>
              <li><button className="dropdown-item" onClick={() => execCommand("formatBlock", "h2")}>Heading 2</button></li>
              <li><button className="dropdown-item" onClick={() => execCommand("formatBlock", "h3")}>Heading 3</button></li>
              <li><hr className="dropdown-divider" /></li>
              <li><button className="dropdown-item" onClick={() => execCommand("formatBlock", "p")}>Normal</button></li>
            </ul>
          </div>

          <Separator />

          {/* Lists */}
          <div className="btn-group btn-group-sm me-1">
            <ToolbarButton command="insertUnorderedList" icon="list-ul" title="Bullet List" />
            <ToolbarButton command="insertOrderedList" icon="list-ol" title="Numbered List" />
          </div>

          <Separator />

          {/* Indentation */}
          <div className="btn-group btn-group-sm me-1">
            <ToolbarButton command="outdent" icon="text-indent-right" title="Decrease Indent" />
            <ToolbarButton command="indent" icon="text-indent-left" title="Increase Indent" />
          </div>

          <Separator />

          {/* Insert */}
          <div className="btn-group btn-group-sm me-1">
            <ToolbarButton icon="link" title="Insert Link" onClick={handleLink} />
            <ToolbarButton icon="image" title="Insert Image" onClick={handleFileUpload} />
          </div>

          <Separator />

          {/* Misc */}
          <div className="btn-group btn-group-sm">
            <ToolbarButton command="removeFormat" icon="eraser" title="Clear Formatting" />
          </div>
        </div>
      )}

      {/* Editor area */}
      <div
        ref={editorRef}
        className="rich-text-editor-content form-control"
        contentEditable={!readOnly}
        onInput={handleInput}
        onPaste={handlePaste}
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        data-placeholder={placeholder}
        style={{
          minHeight,
          overflowY: "auto",
          borderTopLeftRadius: readOnly ? "0.375rem" : 0,
          borderTopRightRadius: readOnly ? "0.375rem" : 0,
        }}
        suppressContentEditableWarning={true}
      />

      {/* Help text */}
      {!readOnly && (
        <small className="text-muted d-block mt-1">
          <i className="bi bi-info-circle me-1"></i>
          Tip: Paste screenshots directly (Ctrl+V) or drag & drop images
        </small>
      )}
    </div>
  );
};

export default RichTextEditor;